/**
 * PD-17283 — the routes the payment lambda registers with the legacy server,
 * and the values every routing row for them carries.
 *
 * The legacy server sends each payment call (`third-party/call/payment/<alias>`)
 * to an `_api_configuration` row of the tenant database: the calling company's
 * row first, then the tenant-wide `(0, 0)` row. A tenant or company with
 * neither answers 404 before the lambda is ever reached, which is what broke
 * HF's ERP payment import for every company without its own processor.
 *
 * The lambda writes these rows itself when a payment processor is pushed to
 * it. Workd also writes them, without the lambda, as a tenant-wide "none"
 * set: card routes then answer 412 ("please setup a Payment Gateway") and data
 * routes (bulk upsert, summaries, balances) work. That is why the list lives
 * here as well as in the lambda (`src/config/payment-endpoints.ts` in
 * workd-payment-lambda); `payment-lambda-endpoints.lambda-match.test.ts`
 * fails when the two differ, so change both in the same release.
 */

export type PaymentLambdaHttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export interface PaymentLambdaEndpointModel {
	/** The alias callers name: `third-party/call/payment/<alias>`. */
	alias: string;
	method: PaymentLambdaHttpMethod;
	/** The lambda path, appended to the environment's base URL. */
	path: string;
}

/** The `_module` of every payment lambda routing row. */
export const PAYMENT_LAMBDA_MODULE = 'payment';

/** The gateway header value for a scope with no payment processor. */
export const PAYMENT_LAMBDA_GATEWAY_NONE = 'none';

/** The gateway header value that adds `PAYMENT_LAMBDA_CYBERSOURCE_ENDPOINTS`. */
export const PAYMENT_LAMBDA_GATEWAY_CYBERSOURCE = 'cybersource';

/** The RBAC action every payment lambda route is linked to. */
export const PAYMENT_LAMBDA_RBAC_ACTION_NAME = '_$ALL';

/** Registered for every gateway, `none` included. */
export const PAYMENT_LAMBDA_ENDPOINTS: ReadonlyArray<PaymentLambdaEndpointModel> =
	[
		{ alias: 'configuration-post', method: 'POST', path: '/configuration' },
		{ alias: 'configuration-get', method: 'GET', path: '/configuration' },
		{
			alias: 'configuration-status-get',
			method: 'GET',
			path: '/configuration-status',
		},
		{
			alias: 'payment-method-list-get',
			method: 'GET',
			path: '/payment-method',
		},
		{ alias: 'payment-method-post', method: 'POST', path: '/payment-method' },
		{
			alias: 'payment-method-delete',
			method: 'DELETE',
			path: '/payment-method/{paymentGatewayPaymentMethodExternalId}',
		},
		{
			alias: 'payment-method-put',
			method: 'PUT',
			path: '/payment-method/{paymentGatewayPaymentMethodExternalId}',
		},
		{
			alias: 'transaction-create',
			method: 'POST',
			path: '/transaction/authorize',
		},
		{
			alias: 'transaction-refund',
			method: 'POST',
			path: '/transaction/refund',
		},
		{ alias: 'transaction-void', method: 'POST', path: '/transaction/void' },
		{
			alias: 'transaction-capture',
			method: 'POST',
			path: '/transaction/capture',
		},
		{ alias: 'transaction-upsert', method: 'POST', path: '/transaction' },
		{
			alias: 'transaction-bulk-upsert',
			method: 'POST',
			path: '/transactions',
		},
		{
			alias: 'transaction-update-from-payment-gateway',
			method: 'POST',
			path: '/transaction/{transactionId}/update-from-payment-gateway',
		},
		{
			alias: 'transaction-bulk-update-from-payment-gateway',
			method: 'POST',
			path: '/transaction/bulk-update-from-payment-gateway',
		},
		{
			alias: 'transaction-summary',
			method: 'GET',
			path: '/transaction/summary',
		},
	];

/** Registered in addition to `PAYMENT_LAMBDA_ENDPOINTS` for CyberSource only. */
export const PAYMENT_LAMBDA_CYBERSOURCE_ENDPOINTS: ReadonlyArray<PaymentLambdaEndpointModel> =
	[
		{
			alias: 'cybersource-capture-context',
			method: 'POST',
			path: '/cybersource/capture-context',
		},
	];

/**
 * Every alias the lambda can write, whatever the gateway. A routing row whose
 * alias is in here but not in `getPaymentLambdaEndpointsForGateway` for the
 * scope's gateway is left over from an earlier processor and safe to remove;
 * an alias outside this list (a developer's prefixed copy, say) is not ours.
 */
export const PAYMENT_LAMBDA_KNOWN_ALIASES: ReadonlyArray<string> = [
	...PAYMENT_LAMBDA_ENDPOINTS,
	...PAYMENT_LAMBDA_CYBERSOURCE_ENDPOINTS,
].map((endpoint) => endpoint.alias);

/** The routes the lambda registers for one gateway. */
export function getPaymentLambdaEndpointsForGateway(
	gateway: string,
): ReadonlyArray<PaymentLambdaEndpointModel> {
	return gateway === PAYMENT_LAMBDA_GATEWAY_CYBERSOURCE
		? [...PAYMENT_LAMBDA_ENDPOINTS, ...PAYMENT_LAMBDA_CYBERSOURCE_ENDPOINTS]
		: PAYMENT_LAMBDA_ENDPOINTS;
}

/**
 * The deployed payment lambda's base URL. Anything that is not production
 * (staging, and every developer machine) uses the staging lambda, the same
 * rule `getEnvironment()` applies.
 */
export const PAYMENT_LAMBDA_BASE_URLS = {
	production: 'https://rhvyd2w9ud.execute-api.us-east-1.amazonaws.com/Prod',
	staging: 'https://vuk8rltdm8.execute-api.us-east-1.amazonaws.com/Prod',
} as const;

export function getPaymentLambdaBaseUrl(environment: string): string {
	return environment === 'production'
		? PAYMENT_LAMBDA_BASE_URLS.production
		: PAYMENT_LAMBDA_BASE_URLS.staging;
}

/**
 * How the lambda narrows a storefront (public) user's requests to the clients
 * that user belongs to. It is sent to the lambda in the configuration body and
 * stored, JSON-encoded, as the `publicUserResource` header of every route. The
 * `{{_workd__user__id}}` placeholder is kept literally in the stored value; the
 * legacy server renders it with the caller's user id on each call.
 */
export const PAYMENT_LAMBDA_PUBLIC_USER_RESOURCE = {
	query: {
		object: 'object/client_public_user',
		includes: [
			{
				objectName: 'public_user',
				type: 'inner',
				localField: 'fk_public_user__id',
				foreignField: '_id',
			},
		],
		fields: [
			{ resourceId: { $reference: 'client_public_user.fk_client__id' } },
		],
		filter: [
			{
				'public_user.workd__user__id': '{{_workd__user__id}}',
				isClientActive: 1,
			},
		],
	},
	fieldName: 'm_sm_payment_fk_client__id',
};

/**
 * One payment method type as the lambda needs it: its name, its fee and the
 * state codes it is allowed in and charges the fee in.
 */
export interface PaymentLambdaPaymentMethodTypeInputModel {
	paymentMethodType: string;
	processingFeeType: string;
	processingFee: number;
	allowedStateCodes: ReadonlyArray<string>;
	feeStateCodes: ReadonlyArray<string>;
}

export interface PaymentLambdaSupportedPaymentMethodModel {
	states: Array<string>;
	fee: {
		unit: string;
		value: number;
		states: Array<string>;
	};
}

/**
 * The `supportedPaymentMethods` map the lambda stores as a header on every
 * route of a scope: `{ <type>: { states, fee: { unit, value, states } } }`.
 * An empty list gives `{}`, which the lambda reads as "no methods".
 */
export function buildPaymentLambdaSupportedPaymentMethods(
	types: ReadonlyArray<PaymentLambdaPaymentMethodTypeInputModel>,
): Record<string, PaymentLambdaSupportedPaymentMethodModel> {
	// Object.fromEntries defines each key as an own property, so a type name
	// such as `__proto__` cannot reach the object's prototype.
	return Object.fromEntries(
		types.map((type): [string, PaymentLambdaSupportedPaymentMethodModel] => [
			type.paymentMethodType,
			{
				states: [...type.allowedStateCodes],
				fee: {
					unit: type.processingFeeType,
					value: type.processingFee,
					states: [...type.feeStateCodes],
				},
			},
		]),
	);
}
